// Neo's alarm CONFIRMATION CARD (ADR 0161, phase 4) — rendered inside the Neo
// chat panel, one per proposal.
//
// Neo proposes; the owner creates. This card is the whole consent step: it shows
// exactly what would be created (the server-built `display`, so the card can
// never disagree with what the validator accepted), and its Create button posts
// the ORDINARY owner route `POST /v1/boats/:boatId/alerts` with the owner's own
// session. There is no model-issued write anywhere in this path, and no text
// parsing decides anything — the click does.
//
// Cards are ephemeral: nothing is persisted, so a reload clears them (ADR 0161).
// The pure logic (decoding, wording, error translation) lives in
// lib/neoAlarmProposal.ts, which is unit-tested; this file holds state + fetch.
//
// UNITS — KNOWN GAP (ADR 0189). The threshold lines are printed exactly as the
// server built them, in the BOAT's units: the cloud does no unit conversion, and
// `NeoAlarmProposalDisplay.thresholds` is pre-formatted TEXT, so there are no
// numbers here to restate in the account's units. Fixing it belongs on the
// server (build the display against the requesting account's `unitSettings`) or
// in the display shape (carry numbers + categories). Re-deriving the lines here
// is the one thing not to do: the card would then claim something other than
// what Create actually posts. Same gap, same reasoning, in
// `components/alarms/redesign/MateProposalCard.tsx`.

import { useState, type ReactNode } from 'react';

import { api } from '../api';
import {
  proposalCreateErrorMessage,
  proposalCreatedMessage,
  proposalCreatedNeedsAttention,
  proposalSeverityLabel,
  proposalWatchLine,
  type AlarmCreateResponse,
  type NeoAlarmProposal,
} from '../lib/neoAlarmProposal';

/**
 * Fired on window after Neo's card created an alarm — an open Alarms page
 * refetches its snapshot (`useBoatAlerts`), no shared state needed. Mirrors
 * {@link DASHBOARD_UPDATED_EVENT} in NeoChat.tsx.
 */
export const ALARMS_UPDATED_EVENT = 'nm:alarms-updated';

type CardState =
  | { kind: 'idle' }
  | { kind: 'creating' }
  | { kind: 'created'; compileStatus: string | null }
  | { kind: 'error'; message: string }
  | { kind: 'dismissed' };

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="neo-proposal__fact">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

export function NeoAlarmProposalCard({ proposal }: { proposal: NeoAlarmProposal }) {
  const [state, setState] = useState<CardState>({ kind: 'idle' });
  const { display } = proposal;

  async function create(): Promise<void> {
    if (state.kind === 'creating') return;
    setState({ kind: 'creating' });
    try {
      // The proposal's own stable id, NOT a fresh key per click — a second
      // press replays the cached 201 instead of creating a second alarm.
      const res = await api<AlarmCreateResponse>(`/v1/boats/${proposal.boatId}/alerts`, {
        method: 'POST',
        body: proposal.body,
        idempotencyKey: proposal.clientId,
      });
      // `compileStatus: 'pending'` is a SUCCESS — the alarm is saved, it is just
      // not bound to a signal yet. The wording says so rather than claiming the
      // boat is watching.
      setState({ kind: 'created', compileStatus: res.compileStatus });
      window.dispatchEvent(new CustomEvent(ALARMS_UPDATED_EVENT));
    } catch (err) {
      setState({ kind: 'error', message: proposalCreateErrorMessage(err) });
    }
  }

  if (state.kind === 'dismissed') {
    return (
      <p className="neo-proposal-dismissed" data-testid="neo-proposal-dismissed">
        Suggestion dismissed.
      </p>
    );
  }

  const created = state.kind === 'created';
  const busy = state.kind === 'creating';

  return (
    <article className="neo-proposal" data-testid="neo-proposal">
      <p className="neo-proposal__eyebrow">
        {created ? 'Alarm added' : 'Suggested alarm — not created yet'}
      </p>

      <div className="neo-proposal__head">
        <span className={`alarm-sev alarm-sev--${display.severity}`}>
          {proposalSeverityLabel(display.severity)}
        </span>
        <h3 className="neo-proposal__title">{display.title}</h3>
      </div>

      <dl className="neo-proposal__facts">
        <Fact label="Watches">{proposalWatchLine(display)}</Fact>
        {display.thresholds.length > 0 ? (
          <Fact label={display.thresholds.length > 1 ? 'Thresholds' : 'Threshold'}>
            <ul className="neo-proposal__lines">
              {display.thresholds.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </Fact>
        ) : null}
        {display.enabledModes.length > 0 ? (
          <Fact label="Armed in">{display.enabledModes.join(', ')}</Fact>
        ) : null}
        {display.conditions !== undefined ? (
          <Fact label="Only when">{display.conditions}</Fact>
        ) : null}
        {display.bindingPath !== undefined ? (
          <Fact label="Pinned to">{display.bindingPath}</Fact>
        ) : null}
      </dl>

      {created ? (
        <p
          className={`neo-proposal__result${
            proposalCreatedNeedsAttention(state.compileStatus) ? ' is-attention' : ''
          }`}
          role="status"
          data-testid="neo-proposal-created"
        >
          {proposalCreatedMessage(state.compileStatus)}
        </p>
      ) : (
        <>
          {state.kind === 'error' ? (
            <p className="neo-proposal__error" role="alert" data-testid="neo-proposal-error">
              {state.message}
            </p>
          ) : null}
          <div className="neo-proposal__actions">
            <button
              type="button"
              className="neo-proposal__create"
              onClick={() => void create()}
              disabled={busy}
              data-testid="neo-proposal-create"
            >
              {busy ? 'Creating…' : state.kind === 'error' ? 'Try again' : 'Create alarm'}
            </button>
            <button
              type="button"
              className="neo-inline-button"
              onClick={() => setState({ kind: 'dismissed' })}
              disabled={busy}
              data-testid="neo-proposal-dismiss"
            >
              Dismiss
            </button>
          </div>
        </>
      )}
    </article>
  );
}
